import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { LibraryItemHoverButton } from '@/components/Library/LibraryItemHoverButton';
import Loader from '@/components/Loaders/Loader';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { EditableTitle } from '@/components/Title/EditableTitle';
import { toast } from '@/components/toast';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useAddRemoveQueryParams } from '@/hooks/useAddRemoveQueryParams';
import { useDownloadFileMutation } from '@/hooks/useDownloadFileMutation';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import { useIsSubtitlesEnabled } from '@/hooks/useIsSubtitlesEnabled';
import Duplicate from '@/images/Copy.svg';
import CopyLink from '@/images/CopyLink.svg';
import Download from '@/images/Download.svg';
import PenOutlined from '@/images/PenOutlined.svg';
import Remove from '@/images/Remove.svg';
import Share from '@/images/Share.svg';
import Subtitles from '@/images/Subtitles.svg';
import { libraryItemToDelete } from '@/pages/video-library/components/VideoOptionsList';
import { useAnalytics } from '@/services/analytics';
import { createShareRecord, duplicateProject, updateVideo } from '@/services/api';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { useRefetchVideos } from '@/state/video-library/videoLibrary';
import theme from '@/theme';
import { copyToClipboardAsShareUrl, createShareUrl, sendShareMixpanelEvent } from '@/utils/shareUtils';
import { MixPanelEvent, StyleCollection } from '@/utils/types';
import { getItemPresenterTypeAndLabels, isMobile } from '@/utils/utils';
import { itemIsProject, LibraryItem, LibraryItemStatus } from '@d-id/types';
import { Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogTitle } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import ShareMenu from '../../../../components/ShareMenu/ShareMenu';

interface Props {
    video: LibraryItem;
    initialOpen?: boolean;
    isError: boolean;
    isPlayable: boolean;
    showOpenButton?: boolean;
    onPlay?: () => void;
    origin: string;
}

const VideoTitle = styled.div`
    width: 100%;
    text-align: center;
    font-size: 2rem;
    margin: 15px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 2rem;
    white-space: nowrap;
`;

const DownloadButton = styled(Button)<{ primary: boolean; isOnMobile?: boolean }>`
    width: auto;
    height: 38px;
    ${props =>
        props.isOnMobile
            ? `
        width: 4rem !important;
        padding: 0 0.7rem 0 2rem;
        min-width: 0;
        `
            : ''}
    ${[theme.breakpoints.down(780)]}: {
        width: 45%;
    }
`;

const SecondaryActionButton = styled(Button)<{ show?: boolean; isOnMobile?: boolean }>`
    display: ${({ show }) => (show !== false ? 'flex' : 'none')};
    margin-left: auto;
    height: 38px;
    ${props =>
        props.isOnMobile
            ? `
        width: 4rem !important;
        padding: 0 0.9rem 0 2rem;
        min-width: 0;
        `
            : 'min-width: 10rem;'}
    ${[theme.breakpoints.down(780)]}: {
        display: none;
    }
`;

const ActionsGroupContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const StyledDialog = styled(BootstrapDialog)`
    & .MuiPaper-root {
        padding: 20px 38px;
        box-sizing: content-box;
        gap: 22px;
        max-width: 1024px;
        max-height: 720px;
        border-radius: 20px;
        height: 90%;

        @media (max-height: 780px) {
            margin: 120px 0;
            background: #f1f2f6;
        }
    }
`;

export const styles: StyleCollection = {
    svgFill: {
        height: '100%',
        width: '100%',
    },

    actions: {
        gap: '1.8rem',
        padding: '0 0 1rem 0 !important',
        display: 'flex',
        justifyContent: 'space-between !important',
        width: '100%',
    },
    closeIcon: {
        marginLeft: 'auto',
        height: '4.2rem',
        width: '4.2rem',
        color: (theme: Theme) => theme.colors.primary,
    },
    titlestyle: {
        padding: '0',
    },
};
const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

const StyledVideo = styled.video`
    height: calc(100% - 166px);
    ::cue {
        text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px 1px 0 #000,
            1px 1px 0 #000;
    }
`;

export const PlayModal = ({ video, initialOpen, isError, isPlayable, showOpenButton = true, onPlay, origin }: Props) => {
    const mixpanel = useAnalytics();
    const setItemToDelete = useSetRecoilState(libraryItemToDelete);
    const [isOpen, setOpen] = useState(!!initialOpen);
    const [isLoading, setIsLoading] = useState(true);
    const subtitlesEnabled = useIsSubtitlesEnabled();
    const playSubtitlesIsEnabled = useFlag('play_subtitles_is_enabled', false);

    const [videoName, setVideoName] = useState(video.name);
    const { add: addQueryParam, remove: removeQueryParam } = useAddRemoveQueryParams();
    const { refetchVideos } = useRefetchVideos();
    const isOnMobile = isMobile();
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();
    const theme = useTheme();
    const [shareAnchorEl, setShareAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [isEditingAsNew, setIsEditingAsNew] = useState<boolean>(false);
    const isMigratedProject = video?.id?.startsWith('prj_tlk_') || video?.id?.startsWith('prj_clp_');
    const isDuplicatable =
        video && itemIsProject(video) && !isMigratedProject && [LibraryItemStatus.Created, LibraryItemStatus.Done].includes(video.status);
    const { mutateAsync: download } = useDownloadFileMutation(() =>
        mixpanel?.track('download', {
            event: MixPanelEvent.CANCEL,
            from: 'play-modal',
            isDefault: video.is_default,
            videoId: video.id,
            ...getItemPresenterTypeAndLabels(video as LibraryItem),
        })
    );
    const { outOfFreeVideos } = useFreeVideos();
    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);

    useEffect(() => {
        if (video.name) {
            setVideoName(video.name);
        }
    }, [video.name]);

    const handleDuplicateProject = useCallback(async () => {
        if (isEditingAsNew && !isOpen) {
            try {
                const newProjectId = await duplicateProject(video.id);

                mixpanel?.track('duplicate', { event: 'click', from: 'play-modal', videoId: video.id, newVideoId: newProjectId });

                await navigateToAdvancedEditor({
                    projectId: newProjectId,
                    options: {
                        isEditAsNew: true,
                    },
                });
            } catch {
                toast.error({ title: 'Failed to duplicate project' });
            }

            setIsEditingAsNew(false);
        }
    }, [isEditingAsNew, isOpen]);

    useEffect(() => {
        /**
         * https://github.com/mui/material-ui/issues/19607, navigation within material-ui dialog is not supported/buggy...
         * Hence, we need to call navigations outside the dialog, in a useEffect
         */

        handleDuplicateProject();
    }, [handleDuplicateProject]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleRemove = () => {
        mixpanel?.track('delete-video', { event: 'click', from: 'play-modal', videoId: video.id });
        setItemToDelete(video);
        handleClose();
    };

    const handleDownloadTalk = async () => {
        mixpanel?.track('download', {
            event: MixPanelEvent.CLICK,
            from: 'play-modal',
            is_default: video.is_default,
            videoId: video.id,
            ...getItemPresenterTypeAndLabels(video as LibraryItem),
        });
        await download({ dataurl: video.video_url!, filename: videoName ?? 'video', extension: '.mp4' });
    };

    const handleDownloadSubtitles = async () => {
        await download({ dataurl: video.subtitles_url!, filename: videoName ?? 'video', extension: '.srt' });
        mixpanel?.track('export-subtitles', { from: 'video-preview' });
    };

    const handleEditAsNew = () => {
        setIsEditingAsNew(true);

        handleClose();
    };

    const handleVideoLoaded = (event: any) => {
        setIsLoading(false);
        const target = event.target as HTMLVideoElement;
        target.currentTime = 0;
        target.controls = true;
        if (!initialOpen) {
            target.play();
        }
    };

    const updateName = async (name: string) => {
        try {
            await updateVideo(video.id, { name }, video.type);
            await refetchVideos();
        } catch (e) {
            toast.error({ title: 'Failed to update video name, please try again' });
            logger.error('Failed to update video name', { e });
            setVideoName(name);
        }
    };

    useEffect(() => {
        if (isOpen) {
            addQueryParam('video', video.id);
            return () => {
                removeQueryParam('video');
            };
        }
    }, [isOpen]);

    const handleOpenVideo = () => {
        onPlay?.();
        setOpen(true);
    };

    const handleEditDraft = async () => {
        if (outOfFreeVideos) {
            setUpgradeFreePlanModalOpen(true);
            return;
        }
        mixpanel?.track('video-edit', {
            event: MixPanelEvent.CLICK,
            videoId: video?.id,
            from: 'edit-draft',
            editorType: 'editor',
        });
        await navigateToAdvancedEditor({ projectId: video.id });
    };

    const [src, handleClick] = isPlayable ? [undefined, handleOpenVideo] : [PenOutlined, handleEditDraft];

    return (
        <>
            {!isError && showOpenButton && (
                <LibraryItemHoverButton
                    onClick={handleClick}
                    icon={src && <MaskedIcon src={src} size="16px" />}
                    text={isPlayable ? 'Play video' : 'Edit draft'}
                    color={isPlayable ? 'secondary' : 'outlined'}
                />
            )}

            <StyledDialog onClose={handleClose} titlestyle={styles.titlestyle} open={isOpen}>
                {isLoading && (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                )}
                <DialogTitle id={video.presenter_version === 'v2' ? 'clip_v2' : video.presenter_version}>
                    <VideoTitle>
                        <EditableTitle
                            disabled={video.is_default}
                            value={videoName}
                            onChange={setVideoName}
                            fontSize={22}
                            onBlur={value => {
                                mixpanel?.track('video-name', { event: 'click', value, from: 'play-modal' });
                                updateName(value);
                            }}
                        />
                        <IconButton ariaLabel="close" onClick={handleClose} sx={styles.closeIcon}>
                            <CloseIcon sx={styles.svgFill} />
                        </IconButton>
                    </VideoTitle>
                </DialogTitle>
                <StyledVideo
                    preload="auto"
                    onLoadedMetadata={handleVideoLoaded}
                    poster={video.thumbnail_url}
                    controls={false}
                    controlsList="nodownload"
                    crossOrigin="use-credentials"
                    playsInline>
                    <source src={video.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                    {playSubtitlesIsEnabled && <track kind="subtitles" src={video.subtitles_url?.replace('.srt', '.vtt')} default />}
                </StyledVideo>
                <DialogActions sx={styles.actions}>
                    <ActionsGroupContainer>
                        <Button
                            variant="text"
                            color="secondary"
                            startIcon={<img src={CopyLink} />}
                            onClick={() => {
                                createShareRecord(video);
                                copyToClipboardAsShareUrl({ itemId: video.id });
                                sendShareMixpanelEvent({
                                    item: video,
                                    modal: 'play',
                                    to: 'copy',
                                    from: origin,
                                });
                            }}>
                            Copy link
                        </Button>
                        <SecondaryActionButton
                            variant="text"
                            color="secondary"
                            disabled={isEditingAsNew || video.is_default}
                            onClick={handleEditAsNew}
                            startIcon={<MaskedIcon src={Duplicate} size="1.6rem" />}
                            show={isDuplicatable && itemIsProject(video)}
                            isOnMobile={isOnMobile}>
                            {!isOnMobile && 'Edit as new'}
                        </SecondaryActionButton>
                        <SecondaryActionButton
                            variant="text"
                            color="secondary"
                            disabled={!video.subtitles_url}
                            onClick={handleDownloadSubtitles}
                            startIcon={<img src={Subtitles} />}
                            show={subtitlesEnabled && !!video.subtitles}
                            isOnMobile={isOnMobile}>
                            {!isOnMobile && 'Export subtitles (.srt)'}
                        </SecondaryActionButton>
                        <SecondaryActionButton
                            variant="text"
                            color="error"
                            disabled={video.is_default}
                            onClick={handleRemove}
                            startIcon={<MaskedIcon src={Remove} color={theme.colors.error.main} size="24px" />}
                            isOnMobile={isOnMobile}>
                            {!isOnMobile && 'Delete'}
                        </SecondaryActionButton>
                    </ActionsGroupContainer>
                    <ActionsGroupContainer>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<img src={Share} />}
                            onClick={e => {
                                setShareAnchorEl(e.currentTarget);
                            }}>
                            Share
                        </Button>
                        <DownloadButton
                            primary
                            variant="contained"
                            color="primary"
                            onClick={handleDownloadTalk}
                            startIcon={<img src={Download} />}
                            isOnMobile={isOnMobile}>
                            {!isOnMobile && 'Download'}
                        </DownloadButton>
                    </ActionsGroupContainer>
                </DialogActions>
                <ShareMenu
                    createShareUrl={(option: string) => createShareUrl({ itemId: video.id, from: option, type: 'video' })}
                    handleShare={(option: string) => {
                        createShareRecord(video);

                        sendShareMixpanelEvent({
                            item: video,
                            modal: 'play',
                            to: option,
                            from: origin,
                        });
                    }}
                    open={!!shareAnchorEl}
                    anchorEl={shareAnchorEl}
                    anchorOrigin={{ vertical: -20, horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    onClose={() => {
                        setShareAnchorEl(null);
                    }}
                />
            </StyledDialog>
        </>
    );
};
